// Close fill against the regenerated plan; facings are the only currency.
import { buildLayout } from "../buildLayout";
import { GeneratedLayout, StudioDraft, StudioItem, StudioRunGeometry } from "../studioTypes";
import { shelfLayoutItemsOf, shelfLayoutRunsOf } from "../shelfRankSpace";
import { planSlacksOf } from "../bayShare";

const MAX_PASSES = 8;

const hintsFrom = (
  layout: GeneratedLayout,
): Record<string, { shelf: number; hFacings: number }[]> => {
  const hints: Record<string, { shelf: number; hFacings: number }[]> = {};
  for (const placement of Object.values(layout.placements)) {
    hints[placement.skuId] = placement.segments.map((segment) => ({
      shelf: segment.shelf,
      hFacings: segment.hFacings,
    }));
  }
  return hints;
};

export interface FillCloseResult {
  draft: StudioDraft;
  /** The final regeneration itself, so callers audit what ships. */
  layout: GeneratedLayout;
  /** Fill share (0-1) of the final regeneration. */
  fillShare: number;
  grown: number;
  shrunk: number;
}

/** Fill per run: used width over shelf capacity, each LANE capped at the run width so an overfull lane cannot masquerade as fill. Overflow is reported separately, never folded into utilization. */
export const fillByRunOf = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): { run: number; share: number; overflowCm: number }[] => {
  const widthOf = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
  const laneUsed = new Map<string, number>();
  for (const placement of Object.values(layout.placements)) {
    const unit = widthOf.get(placement.skuId) ?? 0;
    for (const segment of placement.segments) {
      const key = `${segment.run}|${segment.shelf}`;
      laneUsed.set(key, (laneUsed.get(key) ?? 0) + segment.hFacings * unit);
    }
  }
  return runs.map((run) => {
    let used = 0;
    let overflowCm = 0;
    for (const shelf of run.shelves) {
      const fill = laneUsed.get(`${run.run}|${shelf.rank}`) ?? 0;
      used += Math.min(fill, run.widthCm);
      overflowCm += Math.max(0, fill - run.widthCm);
    }
    const capacity = run.widthCm * run.shelves.length;
    return { run: run.run, share: capacity > 0 ? used / capacity : 0, overflowCm };
  });
};

/** Aggregate fill share across runs, capped per lane (see fillByRunOf). */
export const fillShareOf = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): number => {
  const perRun = fillByRunOf(layout, items, runs);
  const capacity = runs.reduce((a, run) => a + run.widthCm * run.shelves.length, 0);
  const used = perRun.reduce((a, entry, index) => {
    const run = runs[index];
    return a + entry.share * run.widthCm * run.shelves.length;
  }, 0);
  return capacity > 0 ? used / capacity : 0;
};

export const closeFillGap = (
  allItems: readonly StudioItem[],
  allRuns: readonly StudioRunGeometry[],
  draftIn: StudioDraft,
  targetShare: number,
): FillCloseResult => {
  const items = shelfLayoutItemsOf(allItems);
  const runs = shelfLayoutRunsOf(allRuns);
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const capacity = new Map(runs.map((run) => [run.run, run.widthCm]));
  const slack = planSlacksOf(runs);
  let grown = 0;
  let shrunk = 0;

  // PIN: the draft becomes a fixpoint of its own regeneration.
  let draft: StudioDraft = {
    ...draftIn,
    shelfHints: hintsFrom(buildLayout(items, draftIn, runs)),
  };

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const layout = buildLayout(items, draft, runs);
    // Re-pin EVERY pass: a single up-front pin is not a fixpoint, and grown facings get shed right back by the re-deal.
    draft = { ...draft, shelfHints: hintsFrom(layout) };
    const laneFill = new Map<string, number>();
    for (const placement of Object.values(layout.placements)) {
      const unit = bySku.get(placement.skuId)?.facingWidthCm ?? 0;
      for (const segment of placement.segments) {
        const key = `${segment.run}|${segment.shelf}`;
        laneFill.set(key, (laneFill.get(key) ?? 0) + segment.hFacings * unit);
      }
    }
    const over = [...laneFill.entries()].filter(
      ([key, fill]) => fill > (capacity.get(Number(key.split("|")[0])) ?? Infinity) + slack.fill,
    );
    const share = fillShareOf(layout, items, runs);
    // EVERY run must reach the band, not the aggregate: one aisle at 83% hides behind a 95% average.
    const perRun = fillByRunOf(layout, items, runs);
    if (over.length === 0 && perRun.every((entry) => entry.share >= targetShare)) {
      return { draft, layout, fillShare: share, grown, shrunk };
    }

    // TRADE, by editing hint facings in place.
    const hints: Record<string, { shelf: number; hFacings: number }[]> = Object.fromEntries(
      Object.entries(draft.shelfHints).map(([skuId, rows]) => [
        skuId,
        rows.map((row) => ({ ...row })),
      ]),
    );
    const rowsOn = (runId: number, shelf: number) => {
      const out: {
        skuId: string;
        row: { shelf: number; hFacings: number };
        unit: number;
        est: number;
      }[] = [];
      for (const [skuId, rows] of Object.entries(hints)) {
        const item = bySku.get(skuId);
        if (!item || item.run !== runId) continue;
        for (const row of rows) {
          if (row.shelf === shelf) {
            out.push({
              skuId,
              row,
              unit: item.facingWidthCm,
              est: (item.salesAmount ?? 0) / Math.max(slack.edge, item.facingWidthCm),
            });
          }
        }
      }
      return out;
    };
    let changed = false;
    for (const [key, fill] of laneFill) {
      const runId = Number(key.split("|")[0]);
      const shelf = Number(key.split("|")[1]);
      const cap = capacity.get(runId) ?? Infinity;
      if (fill > cap + slack.fill) {
        // shrink the worst seller per cm, floor one facing
        let room = fill - cap;
        const rows = rowsOn(runId, shelf).sort((a, b) => a.est - b.est);
        for (const entry of rows) {
          while (room > slack.fill && entry.row.hFacings > 1) {
            entry.row.hFacings -= 1;
            room -= entry.unit;
            shrunk += 1;
            changed = true;
          }
          if (room <= slack.fill) break;
        }
      } else if (fill < cap - slack.fill) {
        // grow the best seller per cm into the room
        let room = cap - fill;
        const rows = rowsOn(runId, shelf).sort((a, b) => b.est - a.est);
        for (let guard = 0; guard < 200 && room > slack.fill; guard++) {
          const pick = rows.find((entry) => {
            const total = hints[entry.skuId].reduce((sum, row) => sum + row.hFacings, 0);
            const limit = bySku.get(entry.skuId)?.maxFacings ?? Infinity;
            return entry.unit > 0 && entry.unit <= room + slack.fill && total + 1 <= limit;
          });
          if (!pick) break;
          pick.row.hFacings += 1;
          room -= pick.unit;
          grown += 1;
          changed = true;
        }
      }
    }
    if (!changed) {
      return { draft, layout, fillShare: share, grown, shrunk };
    }
    draft = { ...draft, shelfHints: hints };
  }
  const finalLayout = buildLayout(items, draft, runs);
  return {
    draft,
    layout: finalLayout,
    fillShare: fillShareOf(finalLayout, items, runs),
    grown,
    shrunk,
  };
};
